import { useEffect, useRef, useState } from "react";
import { theme, FONT_RUNE } from "../theme";
import { useAuth } from "../../auth/useAuth";
import { LoginScreen } from "./LoginScreen";
import { SignupScreen } from "./SignupScreen";
import { ProfileScreen } from "./ProfileScreen";
import { GameHistoryScreen } from "./GameHistoryScreen";
import { GameReplayScreen } from "./GameReplayScreen";

type Open =
  | { kind: "none" }
  | { kind: "login" }
  | { kind: "signup" }
  | { kind: "profile" }
  | { kind: "history" }
  | { kind: "replay"; gameId: string };

/**
 * Floating account widget. Renders one of three states:
 *   - logged in   → initials-circle button that opens a small dropdown (Profile / Log out)
 *   - logged out  → "Sign in" pill that opens the LoginScreen
 *   - loading     → empty placeholder (no flash before auth state resolves)
 *
 * Sized to slot into the top-right of any screen via the host's own layout.
 */
export function AccountChip({ compact = false }: { compact?: boolean }) {
  const { user, loading, logout } = useAuth();
  const [open, setOpen] = useState<Open>({ kind: "none" });
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) return;
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    window.addEventListener("mousedown", handler);
    return () => window.removeEventListener("mousedown", handler);
  }, [menuOpen]);

  if (loading && !user) {
    // Render a placeholder shape so the layout doesn't jump when auth resolves.
    return <div style={{ width: 36, height: 36 }} />;
  }

  return (
    <>
      <div style={{ position: "relative" }} ref={menuRef}>
        {user ? (
          <button
            onClick={() => setMenuOpen((o) => !o)}
            title={user.displayName ?? user.username}
            style={{
              width: 36,
              height: 36,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: 999,
              fontFamily: FONT_RUNE,
              fontSize: 13,
              letterSpacing: 0.5,
              background: theme.accent,
              color: theme.bg,
              border: `1px solid ${theme.accent}`,
              cursor: "pointer",
            }}
            aria-label={`Signed in as ${user.username}`}
          >
            {initialsFor(user.displayName ?? user.username)}
          </button>
        ) : compact ? (
          <button
            onClick={() => setOpen({ kind: "login" })}
            style={{
              padding: "6px 14px",
              minHeight: 36,
              borderRadius: 999,
              fontFamily: FONT_RUNE,
              fontSize: 12,
              letterSpacing: 1,
              background: theme.boardEdge,
              color: theme.parchment,
              border: `1px solid ${theme.inkSoft}`,
              cursor: "pointer",
            }}
          >
            Sign in
          </button>
        ) : (
          <div style={{ display: "flex", gap: 6 }}>
            <button
              onClick={() => setOpen({ kind: "login" })}
              style={{
                padding: "8px 14px",
                minHeight: 40,
                borderRadius: 6,
                fontFamily: FONT_RUNE,
                fontSize: 12,
                letterSpacing: 1,
                background: theme.boardEdge,
                color: theme.parchment,
                border: `1px solid ${theme.inkSoft}`,
                cursor: "pointer",
              }}
            >
              Sign in
            </button>
            <button
              onClick={() => setOpen({ kind: "signup" })}
              style={{
                padding: "8px 14px",
                minHeight: 40,
                borderRadius: 6,
                fontFamily: FONT_RUNE,
                fontSize: 12,
                letterSpacing: 1,
                background: theme.accent,
                color: theme.bg,
                border: `1px solid ${theme.accent}`,
                cursor: "pointer",
              }}
            >
              Sign up
            </button>
          </div>
        )}

        {menuOpen && user && (
          <div
            style={{
              position: "absolute",
              right: 0,
              top: "calc(100% + 6px)",
              minWidth: 180,
              background: theme.bgElev,
              border: `1px solid ${theme.boardEdge}`,
              borderRadius: 8,
              boxShadow: "0 8px 22px rgba(0,0,0,0.55)",
              padding: 6,
              display: "flex",
              flexDirection: "column",
              gap: 2,
              zIndex: 100,
            }}
          >
            <DropdownItem
              onClick={() => {
                setMenuOpen(false);
                setOpen({ kind: "profile" });
              }}
            >
              Profile
            </DropdownItem>
            <DropdownItem
              onClick={() => {
                setMenuOpen(false);
                setOpen({ kind: "history" });
              }}
            >
              Your games
            </DropdownItem>
            <DropdownItem
              onClick={() => {
                setMenuOpen(false);
                void logout();
              }}
            >
              Log out
            </DropdownItem>
          </div>
        )}
      </div>

      {open.kind === "login" && (
        <LoginScreen
          onClose={() => setOpen({ kind: "none" })}
          onSwitchToSignup={() => setOpen({ kind: "signup" })}
        />
      )}
      {open.kind === "signup" && (
        <SignupScreen
          onClose={() => setOpen({ kind: "none" })}
          onSwitchToLogin={() => setOpen({ kind: "login" })}
        />
      )}
      {open.kind === "profile" && (
        <ProfileScreen onClose={() => setOpen({ kind: "none" })} />
      )}
      {open.kind === "history" && (
        <GameHistoryScreen
          onClose={() => setOpen({ kind: "none" })}
          onOpenReplay={(gameId) => setOpen({ kind: "replay", gameId })}
        />
      )}
      {open.kind === "replay" && (
        <GameReplayScreen
          gameId={open.gameId}
          onClose={() => setOpen({ kind: "history" })}
        />
      )}
    </>
  );
}

function DropdownItem({ onClick, children }: { onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      style={{
        textAlign: "left",
        padding: "8px 12px",
        background: "transparent",
        color: theme.parchment,
        border: "none",
        borderRadius: 4,
        cursor: "pointer",
        fontSize: 13,
        fontFamily: FONT_RUNE,
        letterSpacing: 0.5,
      }}
      onMouseEnter={(e) => (e.currentTarget.style.background = theme.boardEdge)}
      onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
    >
      {children}
    </button>
  );
}

function initialsFor(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 0 || !parts[0]) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}
